import { ReferencedInquirySummary, referencedInquiryLabel } from './ReferencedInquirySummary';

import { Button, ButtonText } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import type { ReferencedInquiry } from '@/services/conversations/referencedInquiry';
import { TOUCH_TARGET_MIN_H } from '@/util/accessibleTouchTarget';

type ReferencedInquiryCardProps = {
  inquiry: ReferencedInquiry;
  /** Opens the inquiry; the card shows no button without it. */
  onOpen?: () => void;
};

/**
 * The inquiry a support message links to, inside the message. Everything it
 * shows rides on the message: the API snapshots the details when the message
 * is sent, so the card makes no request and renders complete over realtime.
 * Do not add a fetch to fill a missing detail. It never shows whether the
 * inquiry is open or closed: that would be the state when the message was sent,
 * which goes stale.
 *
 * Both sides can open it. The member opens their own inquiry; staff acting as
 * Primum Support, who are not part of it, get it read-only, with no composer,
 * close actions or read receipt.
 */
export function ReferencedInquiryCard({ inquiry, onOpen }: ReferencedInquiryCardProps) {
  return (
    <VStack className="mt-3 gap-2 rounded-xl border border-outline-200 bg-background-50 p-3">
      <Text className="font-semibold text-xs text-typography-700">Linked inquiry</Text>

      <ReferencedInquirySummary inquiry={inquiry} showState={false} />

      {onOpen ? (
        <VStack className="items-start">
          <Button
            size="sm"
            className={TOUCH_TARGET_MIN_H}
            onPress={onOpen}
            accessibilityLabel={`Open inquiry: ${referencedInquiryLabel(inquiry, { showState: false })}`}>
            <ButtonText>Open inquiry</ButtonText>
          </Button>
        </VStack>
      ) : null}
    </VStack>
  );
}
